import { useEffect } from 'react';

/**
 * Lightweight SEO hook: sets <title>, <meta name="description">, canonical link,
 * and OG tags on the rendered SPA page. For social share previews, share via
 * the matching /api/share/why/{slug} URL — that returns server-rendered OG HTML.
 *
 * Usage:
 *   useSEO({
 *     title: 'Verified Agents — WakaCasa',
 *     description: 'Win more clients with the WakaCasa Verified badge…',
 *     canonical: '/why/verified-agents',
 *     image: '/sales/agent-verification.jpeg',
 *   });
 */
export default function useSEO({ title, description, canonical, image }) {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      if (!value) return null;
      let el = document.head.querySelector(selector);
      if (!el) {
        // Parse selector like meta[property="og:image"] → tag=meta, key=property, val=og:image
        const m = selector.match(/^(\w+)\[(\w+)="([^"]+)"\]$/);
        if (!m) return null;
        el = document.createElement(m[1]);
        el.setAttribute(m[2], m[3]);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return { el, prev, attr };
    };

    const updates = [];
    if (description) updates.push(setMeta('meta[name="description"]', 'content', description));
    if (description) updates.push(setMeta('meta[property="og:description"]', 'content', description));
    if (title) updates.push(setMeta('meta[property="og:title"]', 'content', title));
    updates.push(setMeta('meta[property="og:type"]', 'content', 'website'));
    updates.push(setMeta('meta[property="og:site_name"]', 'content', 'WakaCasa'));

    let imageAbs = image;
    if (image && image.startsWith('/')) {
      imageAbs = window.location.origin + image;
    }
    if (imageAbs) {
      updates.push(setMeta('meta[property="og:image"]', 'content', imageAbs));
      updates.push(setMeta('meta[name="twitter:image"]', 'content', imageAbs));
    }
    if (title) updates.push(setMeta('meta[name="twitter:title"]', 'content', title));
    if (description) updates.push(setMeta('meta[name="twitter:description"]', 'content', description));
    updates.push(setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image'));

    let canonicalAbs = canonical;
    if (canonical && canonical.startsWith('/')) {
      canonicalAbs = window.location.origin + canonical;
    }
    if (canonicalAbs) {
      updates.push(setMeta('meta[property="og:url"]', 'content', canonicalAbs));
      let linkEl = document.head.querySelector('link[rel="canonical"]');
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.setAttribute('rel', 'canonical');
        document.head.appendChild(linkEl);
      }
      const prevHref = linkEl.getAttribute('href');
      linkEl.setAttribute('href', canonicalAbs);
      updates.push({ el: linkEl, prev: prevHref, attr: 'href' });
    }

    return () => {
      document.title = previousTitle;
      updates.forEach((u) => {
        if (!u) return;
        if (u.prev == null) {
          u.el.remove();
        } else {
          u.el.setAttribute(u.attr, u.prev);
        }
      });
    };
  }, [title, description, canonical, image]);
}
